import { message, type FormRule } from "antd";

/**
 * Centralized validation utilities for forms
 */

/**
 * Phone number validation regex
 * Supports formats:
 * - +1234567890
 * - +91 12345 67890
 * - (123) 456-7890
 * - 123-456-7890
 * - 1234567890
 */
const PHONE_REGEX = /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/;

/**
 * URL/Domain validation regex
 * Supports:
 * - http://example.com
 * - https://example.com
 * - example.com
 * - subdomain.example.com
 */
const URL_REGEX = /^(https?:\/\/)?(localhost|([\w-]+\.)+[\w-]+)(:\d+)?(\/[\w-./?%&=]*)?$/;

/**
 * Email validation regex (basic)
 */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Validates a phone number
 * @param phoneNumber - The phone number to validate
 * @returns true if valid, false otherwise
 */
export const validatePhoneNumber = (phoneNumber: string): boolean => {
  if (!phoneNumber || phoneNumber.trim() === "") return true; // Optional field
  return PHONE_REGEX.test(phoneNumber.trim());
};

/**
 * Validates a URL or domain
 * @param url - The URL to validate
 * @returns true if valid, false otherwise
 */
export const validateUrl = (url: string): boolean => {
  if (!url || url.trim() === "") return false;
  return URL_REGEX.test(url.trim());
};

/**
 * Validates an email address
 * @param email - The email to validate
 * @returns true if valid, false otherwise
 */
export const validateEmail = (email: string): boolean => {
  if (!email || email.trim() === "") return false;
  return EMAIL_REGEX.test(email.trim());
};

/**
 * Ant Design Form validator for phone numbers
 * Use with Form.Item rules prop
 */
export const phoneValidator = (_: unknown, value: string) => {
  if (!value || value.trim() === "") {
    return Promise.resolve();
  }
  if (validatePhoneNumber(value)) {
    return Promise.resolve();
  }
  return Promise.reject(new Error("Please enter a valid phone number"));
};

/**
 * Ant Design Form validator for URLs
 * Use with Form.Item rules prop
 */
export const urlValidator = (_: unknown, value: string) => {
  if (!value || value.trim() === "") {
    return Promise.resolve();
  }
  if (validateUrl(value)) {
    return Promise.resolve();
  }
  return Promise.reject(new Error("Please enter a valid URL or domain"));
};

/**
 * Ant Design Form validator for email
 * Use with Form.Item rules prop
 */
export const emailValidator = (_: unknown, value: string) => {
  if (!value || value.trim() === "") {
    return Promise.reject(new Error("Email is required"));
  }
  if (validateEmail(value)) {
    return Promise.resolve();
  }
  return Promise.reject(new Error("Please enter a valid email address"));
};

// [start, end) slice of the value, the pattern it must match, and the message if it doesn't
type Segment = [start: number, end: number, pattern: RegExp, message: string];

/**
 * Builds an AntD validator for a fixed-length, position-based ID (PAN, GSTIN) that tells the
 * user which part is wrong. Empty values pass (the `required` rule owns that); the length is
 * checked first, then segments in order, first failure wins.
 */
const segmentValidator = (label: string, length: number, segments: Segment[]) => (_: unknown, value: string) => {
  if (!value || value.trim() === "") return Promise.resolve();
  if (value.length !== length) {
    return Promise.reject(new Error(`${label} must be exactly ${length} characters (you entered ${value.length})`));
  }
  const failed = segments.find(([start, end, pattern]) => !pattern.test(value.slice(start, end)));
  return failed ? Promise.reject(new Error(failed[3])) : Promise.resolve();
};

/**
 * Ant Design Form validator for PAN (AAAAA9999A)
 * Equivalent to /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/ but with a position-specific message
 */
export const panNumberValidator = segmentValidator("PAN Number", 10, [
  [0, 5, /^[A-Z]+$/, "PAN characters 1-5 must be letters (A-Z)"],
  [5, 9, /^[0-9]+$/, "PAN characters 6-9 must be digits (0-9)"],
  [9, 10, /^[A-Z]$/, "PAN character 10 must be a letter (A-Z)"],
]);

/**
 * Ant Design Form validator for GSTIN (99AAAAA9999A1Z1)
 * Equivalent to /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/ but with a position-specific message
 */
export const gstNumberValidator = segmentValidator("GST Number", 15, [
  [0, 2, /^[0-9]+$/, "GSTIN characters 1-2 must be digits (state code)"],
  [2, 7, /^[A-Z]+$/, "GSTIN characters 3-7 must be letters (first 5 letters of PAN)"],
  [7, 11, /^[0-9]+$/, "GSTIN characters 8-11 must be digits (PAN digits)"],
  [11, 12, /^[A-Z]$/, "GSTIN character 12 must be a letter (PAN last letter)"],
  [12, 13, /^[1-9A-Z]$/, "GSTIN character 13 must be 1-9 or a letter (entity number)"],
  [13, 14, /^Z$/, "GSTIN character 14 must be the letter Z"],
  [14, 15, /^[0-9A-Z]$/, "GSTIN character 15 must be a digit or letter (checksum)"],
]);

/**
 * Props for a `<Form.Item name="pin">` next to a `country` field: for India the pin is digits-only
 * (typing and pasting), capped at and validated to exactly 6 digits; other countries stay free-form.
 * `normalize` only runs on input, so the rule also covers values loaded from the database.
 */
export const pinItemProps = {
  normalize: (value: string, _prev: string, all: { country?: string | null }) =>
    value && all.country === "India" ? value.replace(/\D/g, "").slice(0, 6) : value,
  rules: [
    { required: true, message: "Pin is required" },
    ({ getFieldValue }) => ({
      validator: (_: unknown, value?: string) =>
        !value || getFieldValue("country") !== "India" || /^\d{6}$/.test(value)
          ? Promise.resolve()
          : Promise.reject(new Error("Pin must be exactly 6 digits")),
    }),
  ] as FormRule[],
};

/**
 * Props for a `<Form.Item name="state">` next to a `country` field: required only for India and the
 * United States. Many other countries have no NetSuite state list, so it's optional there.
 */
export const stateItemProps = {
  rules: [
    ({ getFieldValue }) => ({
      required: ["India", "United States"].includes(getFieldValue("country")),
      message: "State is required",
    }),
  ] as FormRule[],
};

/**
 * Form `onFinishFailed` handler: tells the user what's missing (naming any required
 * document uploads) and scrolls the first invalid field into view.
 */
export const handleFormValidationFailed = (info: { errorFields: { name: (string | number)[]; errors: string[] }[] }) => {
  const missingDocs = info.errorFields
    .filter((f) => typeof f.name[0] === "string" && (f.name[0].endsWith("File") || f.name[0] === "customDocuments"))
    .map((f) => f.errors[0]?.replace(/ is required$/, "") ?? "");
  const otherCount = info.errorFields.length - missingDocs.length;

  if (missingDocs.length > 0) {
    const docList = missingDocs.filter(Boolean).join(", ");
    message.error(
      `Please upload the required document(s) before saving: ${docList}` +
        (otherCount > 0 ? " (and fill the other highlighted fields)" : ""),
    );
  } else {
    message.error("Please fill in all the required fields before saving");
  }

  const firstName = info.errorFields[0]?.name[0];
  setTimeout(() => {
    const target =
      document.querySelector(`.ant-modal [data-doc-field="${firstName}"]`) ??
      document.querySelector(".ant-modal .ant-form-item-has-error");
    target?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, 0);
};
